import React, { Fragment, ReactNode } from 'react';
import { Heading01, Heading04, Heading05 } from '@uds/typography';
import PaddingBox, { Side } from '@uds/padding-box';
import CallToAction from 'components/common/CallToAction';
import { Size as ButtonSize } from '@uds/button';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import { useBreakpoints } from '@ux/responsive';
import { Layout } from 'domain/constants/layout';
import { Colorway } from '@uds/theme-utils';
import { CTA } from 'core/ribbons/cta';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';

import useStylesheet, { getClassNames } from './Body.stylesheet';

export interface OuterProps {
  hBaseline?: number;
  text: {
    precursor?: ShortcodeProps;
    title?: ShortcodeProps;
    body?: ShortcodeProps;
  };
  additionalText?: string | ReactNode;
  footerText?: string | ReactNode;
  ctas: Array<CTA>;
  colorway: Colorway;
  layout: Layout;
  ribbonId: string;
}

declare const cta: CTA;
declare const i: number;

const PureBody = ({
  hBaseline = 1,
  text,
  additionalText,
  footerText,
  ctas,
  colorway,
  layout,
  ribbonId,
}: OuterProps) => {
  const { isMinSm } = useBreakpoints();
  const { body, precursor, title } = text;
  const hasBodyText = !isNilOrEmpty(body);
  const hasCta = !isNilOrEmpty(ctas);
  const hasPrecursor = !isNilOrEmpty(precursor);
  const hasTitle = !isNilOrEmpty(title);
  const hasFooterText = !isNilOrEmpty(footerText);
  const { styles } = useStylesheet({
    isMinSm,
    layout,
    colorway,
  });
  const classNames = getClassNames();
  return (
    <Fragment>
      <PaddingBox>
        <div style={styles.root}>
          <Choose>
            <When condition={hasPrecursor}>
              <Heading05 className={classNames.precursor} depth={hBaseline}>
                <Shortcode against={colorway} code={precursor} />
              </Heading05>
              {hasTitle && (
                <Heading01 depth={hBaseline + 1}>
                  <Shortcode against={colorway} code={title} />
                </Heading01>
              )}
            </When>
            <Otherwise>
              {hasTitle && (
                <Heading01 depth={hBaseline}>
                  <Shortcode against={colorway} code={title} />
                </Heading01>
              )}
            </Otherwise>
          </Choose>
          {hasBodyText && (
            <Heading04 component="p" className={classNames.subtitle}>
              <Shortcode against={colorway} layout={layout} code={text.body} />
            </Heading04>
          )}
          {additionalText}
          {hasCta && (
            <div style={styles.ctas}>
              <For each="cta" index="i" of={ctas}>
                <span className={classNames.cta} key={i}>
                  <CallToAction size={ButtonSize.LG} ribbonId={ribbonId} index={i} {...cta} />
                </span>
              </For>
            </div>
          )}
        </div>
      </PaddingBox>
      {hasFooterText && (
        <PaddingBox side={Side.X}>
          <div style={styles.footerText}>{footerText}</div>
        </PaddingBox>
      )}
    </Fragment>
  );
};

PureBody.displayName = 'BillboardBody';

export default PureBody;
